import React, { useState } from "react";
import { Mail, ShieldAlert, Check } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { guestEmailService } from "@core/lib/guestEmailService";
import { sendGuestOwnerLink } from "@core/lib/edgeFunctionClient";
import { useToast } from "@core/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { logger } from "@core/lib/logger";

interface SafeguardSectionProps {
  /** ID du sondage créé — nécessaire pour envoyer le lien de résultats.
   *  Sans lui (appelants existants non encore migrés), seule la sauvegarde
   *  RGPD historique a lieu, sans email. */
  pollId?: string;
}

export function SafeguardSection({ pollId }: SafeguardSectionProps) {
  const [email, setEmail] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const { toast } = useToast();
  const { t, i18n } = useTranslation("polls");

  const handleSaveEmail = async () => {
    if (!email || !email.includes("@")) {
      toast({
        title: t("safeguard.toast.invalidEmail.title"),
        description: t("safeguard.toast.invalidEmail.description"),
        variant: "destructive",
      });
      return;
    }

    guestEmailService.saveGuestEmail(email);
    setIsSaved(true);

    // L'email de retour n'est envoyé que si on connaît le sondage concerné.
    // Un échec d'envoi ne doit pas remettre en cause la sauvegarde RGPD déjà
    // effectuée (comportement dégradé, pas une erreur bloquante pour
    // l'utilisateur qui a déjà vu la confirmation).
    if (pollId) {
      try {
        await sendGuestOwnerLink({ pollId, email, lang: i18n.language });
      } catch (error) {
        logger.warn("Failed to send guest owner link email", "api", {
          error,
        });
      }
    }

    toast({
      title: t("safeguard.toast.saved.title"),
      description: t("safeguard.toast.saved.description"),
    });
  };

  if (isSaved) {
    return (
      <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex items-center gap-3">
        <Check className="w-5 h-5 text-success" />
        <div>
          <p className="text-sm font-medium text-success">
            {t("safeguard.confirmed.title")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("safeguard.confirmed.description")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 bg-warning/10 border border-warning/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-warning">
            {t("safeguard.heading")}
          </h4>
          <p className="text-xs text-warning/90">{t("safeguard.intro")}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder={t("safeguard.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-9 h-9 text-sm text-foreground"
            data-testid="guest-email-input"
          />
        </div>
        <Button
          size="sm"
          onClick={handleSaveEmail}
          className="bg-warning hover:bg-warning/90 text-warning-foreground h-9"
          data-testid="safeguardsection-sauvegarder"
        >
          {t("safeguard.save")}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground ml-1">
        {t("safeguard.gdprNote")}
      </p>
    </div>
  );
}
